import { notification, message } from 'antd';
import TaskItem from '../components/TaskItem';
import TaskModal from '../components/TaskModal';
import TaskActivities from '../components/TaskActivities';
import { Draggable, Droppable } from '@hello-pangea/dnd';
import { useSearchParams } from 'react-router-dom';

import type { Task } from '../../types';
import type { RootState } from '../store';

import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { usePrompt } from '../hooks/usePrompt';
import { useConfirm } from '../hooks/useConfirm';
import useBoolean from '../hooks/useBoolean';

import { 
    useUpdateTaskMutation,
    useDeleteTaskMutation,
    useUpdateStatusMutation,
    useAddTaskImageMutation,
    useDeleteTaskImageMutation,
    useAddTaskLinkMutation,
    useDeleteTaskLinkMutation,
    useUpdateTagsMutation,
    useUpdatePriorityMutation,
} from '../store/tasks';

import { SUPPORT_TAGS } from '../constants';
import {
    escapeRegExp,
    escapeHtml,
    getTaskTitle,
    highlightPlainText,
} from '../utils/text';
import {
    decorateContentLinks,
    extractTaskUrls,
    linksNotIn,
} from '../utils/links';
import { getVisibleTasks } from '../utils/taskFilters';

const TASK_QUERY_KEY = 'task';

interface TasksListProps {
    todoId: number;
    tasks: Task[];
    columnId: number;
}

function buildTaskShareUrl(taskId: number): string {
    const url = new URL(window.location.href);
    url.searchParams.set(TASK_QUERY_KEY, String(taskId));
    return url.toString();
}

const TasksList = ({ columnId, tasks, todoId }: TasksListProps) => {
    const [api, contextHolder] = notification.useNotification();
    const { prompt, promptModal } = usePrompt();
    const { confirm, confirmModal } = useConfirm();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const [ deleteTask ] = useDeleteTaskMutation();
    const [ updateTask ] = useUpdateTaskMutation();
    const [ updateTaskStatus ] = useUpdateStatusMutation();
    const [ updateTaskPriority ] = useUpdatePriorityMutation();
    const [ addTaskImage ] = useAddTaskImageMutation();
    const [ deleteTaskImage ] = useDeleteTaskImageMutation();
    const [ addTaskLink ] = useAddTaskLinkMutation();
    const [ deleteTaskLink ] = useDeleteTaskLinkMutation();
    const [ updateTags ] = useUpdateTagsMutation();

    const settings = useSelector((state: RootState) => state.settings);

    const isOpenEditModal = useBoolean();
    const isTaskActivitiesOpen = useBoolean();
    const [ editTask, setEditTask ] = useState<Task>();
    const [ activitiesTask, setActivitiesTask ] = useState<Task>();

    const taskQueryId = useMemo(() => {
        const raw = searchParams.get(TASK_QUERY_KEY);
        if (!raw) {
            return null;
        }
        const id = Number(raw);
        return Number.isFinite(id) ? id : null;
    }, [searchParams]);

    const setTaskInUrl = (taskId: number | null) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (taskId == null) {
                next.delete(TASK_QUERY_KEY);
            } else {
                next.set(TASK_QUERY_KEY, String(taskId));
            }
            return next;
        }, { replace: true });
    };

    const openTaskModal = (task: Task, syncUrl = true) => {
        setEditTask(task);
        isOpenEditModal.set(true);
        if (syncUrl) {
            setTaskInUrl(task.taskId);
        }
    };

    const closeTaskModal = (clearUrl = true) => {
        isOpenEditModal.set(false);
        if (clearUrl && taskQueryId != null && editTask && Number(editTask.taskId) === taskQueryId) {
            setTaskInUrl(null);
        }
    };

    // Открытие карточки по ссылке ?task=
    // Модалка живёт в колонке, где сейчас задача: при переносе статуса
    // закрываем её здесь, чтобы соседняя колонка не открыла вторую.
    useEffect(() => {
        if (taskQueryId == null) {
            if (isOpenEditModal.value) {
                isOpenEditModal.set(false);
            }
            return;
        }

        const found = tasks.find((task) => Number(task.taskId) === taskQueryId);
        if (!found) {
            if (isOpenEditModal.value) {
                isOpenEditModal.set(false);
            }
            return;
        }

        setEditTask(found);
        if (!isOpenEditModal.value) {
            isOpenEditModal.set(true);
        }
    }, [taskQueryId, tasks]);

    // Синхронизируем открытую модалку с актуальными данными задачи из стора
    useEffect(() => {
        if (!editTask?.taskId) {
            return;
        }
        const newTaskState = tasks.find((task) => task.taskId === editTask.taskId);
        if (newTaskState) {
            setEditTask(newTaskState);
        }
    }, [tasks, editTask?.taskId]);

    const handleDeleteTask = async (task: Task) => {
        const result = await confirm({
            message: `Вы уверены, что хотите удалить задачу №${ task.taskId }?`,
        });
        if (result) {
            if (taskQueryId === Number(task.taskId)) {
                setTaskInUrl(null);
            }
            deleteTask(task);
            if (editTask?.taskId === task.taskId) {
                isOpenEditModal.set(false);
            }
        }
    };

    const handleCopyTaskLink = async (task: Task, options?: { silent?: boolean }): Promise<boolean> => {
        const shareUrl = buildTaskShareUrl(task.taskId);
        try {
            await navigator.clipboard.writeText(shareUrl);
            if (!options?.silent) {
                message.success('Ссылка на карточку скопирована');
            }
            return true;
        } catch {
            message.error('Не удалось скопировать ссылку');
            return false;
        }
    };

    const filtredTasks = useMemo(() => {
        const query = settings.search;
        const visible = getVisibleTasks(tasks, query, settings.filters);

        return visible.map((task) => {
            const title = getTaskTitle(task);
            let contentMatch = escapeHtml(title);

            if (query) {
                if (query.startsWith('/')) {
                    try {
                        contentMatch = highlightPlainText(title, new RegExp(query.slice(1), 'i'));
                    } catch {
                        contentMatch = escapeHtml(title);
                    }
                } else {
                    contentMatch = highlightPlainText(title, new RegExp(escapeRegExp(query), 'i'));
                }
            }

            return { original: task, contentMatch: decorateContentLinks(contentMatch) };
        });
    }, [settings.search, settings.filters, tasks]);

    const isValidUrl = (url: string): boolean => {
        const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/;
        return urlPattern.test(url);
    };

    const handleAddLink = async (task: Task) => {
        const link = await prompt({
            title: 'Добавление ссылки',
        });

        if (link) {
            if (isValidUrl(link)) {
                await addTaskLink({ ...task, link });
            }
            else {
                api.error({
                    message: 'Ошибка',
                    description: 'Введена некорректная ссылка. Пожалуйста, проверьте формат URL.',
                });
            }
        }
    };

    const handleEdit = (task: Task) => {
        openTaskModal(task, true);
    };

    const handleShowActivities = (task: Task) => {
        isTaskActivitiesOpen.set(true);
        setActivitiesTask(task);
    };

    const handleSaveEdit = (payload: { taskName: string; taskContent: string }) => {
        const task = editTask as Task;
        updateTask({
            taskId: task.taskId,
            todoId: todoId,
            taskName: payload.taskName,
            taskContent: payload.taskContent,
        });

        const extracted = extractTaskUrls({
            taskName: payload.taskName,
            taskContent: payload.taskContent,
        });
        const existing = (task.links || []).map((link) => link.linkPath);
        linksNotIn(extracted, existing).forEach((link) => {
            addTaskLink({ todoId, taskId: task.taskId, link });
        });
    };

    
    const handleAddTag = async (task: Task) => {
        const availableTags = Object.values(SUPPORT_TAGS).filter(
            (tag) => !task.tags.includes(tag),
        );
        const tagNames = await prompt({
            title: 'Добавление тегов',
            tags: availableTags,
            multiple: true,
            placeholder: 'Выберите или введите теги',
        });
        if (tagNames.length > 0) {
            const unique = tagNames.filter((tag) => !task.tags.includes(tag));
            if (unique.length > 0) {
                updateTags({
                    todoId,
                    taskId: task.taskId,
                    tags: [...task.tags, ...unique],
                });
            }
        }
    };

    const handleDeleteTag = (task: Task, index: number) => {
        const newTags = task.tags.filter((el, i) => index !== i);
        updateTags({ todoId, taskId: task.taskId, tags: newTags });
    };

    return (
        <>
            <Droppable
                droppableId={columnId.toString()}
                type="TASK"
                ignoreContainerClipping={true}
            >
                {(dropProvided) => (
                    <div
                        ref={dropProvided.innerRef}
                        {...dropProvided.droppableProps}
                        className="tasks-list"
                    >
                        <div className="dropzone">
                            {
                                filtredTasks.map(({ original: task, contentMatch }, index) => (
                                    <Draggable key={task.taskId} draggableId={task.taskId.toString()} index={index}>
                                        {(dragProvided, dragSnapshot) => (
                                            <TaskItem
                                                provided={dragProvided}
                                                task={task}
                                                isDragging={dragSnapshot.isDragging}
                                                contentMatch={contentMatch}
                                                onAddLink={() => handleAddLink(task)}
                                                onDeleteLink={(linkId) => deleteTaskLink({ ...task, linkId })}
                                                onAddTag={() => handleAddTag(task)}
                                                onDeleteTag={(index) => handleDeleteTag(task, index)}
                                                onAddImage={async (file) => {
                                                    const result = await addTaskImage({ ...task, file }).unwrap();
                                                    return result?.message;
                                                }}
                                                onDeleteImage={(imageId) => deleteTaskImage({ ...task, imageId })}
                                                onEdit={() => handleEdit(task)}
                                                onCopyLink={() => handleCopyTaskLink(task)}
                                                onDelete={() => handleDeleteTask(task)}
                                                onShowActivities={() => handleShowActivities(task)}
                                                onUpdateStatus={(status) => updateTaskStatus({ ...task, status })}
                                            />
                                        )}
                                    </Draggable>
                                ))
                            }
                            {dropProvided.placeholder}
                        </div>
                    </div>
                )}
            </Droppable>

            {contextHolder}
            {promptModal}
            {confirmModal}

            {editTask &&
                <TaskModal
                    isOpen={isOpenEditModal.value}
                    task={editTask}
                    onAddLink={() => handleAddLink(editTask)}
                    onDeleteLink={(linkId) => deleteTaskLink({ ...editTask, linkId })}
                    onAddTag={() => handleAddTag(editTask)}
                    onDeleteTag={(index) => handleDeleteTag(editTask, index)}
                    onAddImage={async (file) => {
                        const result = await addTaskImage({ ...editTask, file }).unwrap();
                        return result?.message;
                    }}
                    onDeleteImage={(imageId) => deleteTaskImage({ ...editTask, imageId })}
                    onDelete={() => handleDeleteTask(editTask)}
                    onShowActivities={() => handleShowActivities(editTask)}
                    onUpdateStatus={(status) => updateTaskStatus({ ...editTask, status })}
                    onClose={() => closeTaskModal(true)}
                    onCopyLink={() => handleCopyTaskLink(editTask, { silent: true })}
                    onSave={(payload) => handleSaveEdit(payload)}
                    onUpdatePriority={(priority) => updateTaskPriority({ ...editTask, priority })}
                />
            }

            {activitiesTask &&
                <TaskActivities
                    open={isTaskActivitiesOpen.value}
                    taskId={activitiesTask.taskId}
                    onClose={() => isTaskActivitiesOpen.set(false)}
                />
            }
        </>
    );
};

export default TasksList;

